import { observer } from 'mobx-react-lite'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { EditPencil, Plus as PlusIcon, Trash } from 'iconoir-react'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import Spinner from 'app/components/Spinner'
import { PageScroll } from 'app/components/layout/PageScroll'
import { TableBody, TableCell, TableRow } from 'app/components/ui/table'
import {
  GROUPED_TABLE_CELL,
  GroupedTable,
  GroupedTableHeaderCard,
  GroupedTableSection,
  type GroupedTableColumn,
} from 'app/components/table/GroupedTable'
import { Tooltip } from 'app/components/Tooltip'
import { cn } from 'app/lib/utils'
import type { CdtCodeMappingType } from 'app/types/entities'
import type { TreatmentCategoryValue } from 'lib/cdtCodeDefaults'
import { formatRange, type UseCdtCodeMappingReturn } from './useCdtCodeMapping'

export type CdtCodeMappingShadcnComponentProps = Omit<
  UseCdtCodeMappingReturn,
  `loading` | `onSearch` | `searchValue`
> & {
  loading?: boolean
  onSearch?: (query: string) => void
  searchValue?: string
}

function cdtColumns(canEditMapping: boolean): GroupedTableColumn[] {
  return [
    { label: `CDT Code` },
    ...(canEditMapping
      ? [{ label: `Actions`, colClassName: `w-[100px]`, headClassName: `text-right` } as GroupedTableColumn]
      : []),
  ]
}

export const CdtCodeMappingShadcnComponent = observer(function CdtCodeMappingShadcnComponent({
  loading,
  filteredCategories,
  groupedMappings,
  categoriesWithMappings,
  canEditMapping,
  onNewMapping,
  onEditMapping,
  onDeleteMapping,
  savingIds,
  disclosureKey,
  onSearch,
  searchValue,
}: CdtCodeMappingShadcnComponentProps) {
  return (
    // Top padding lives on the content, not the scroller: padding on the scroll
    // container would inset the sticky header card and let rows peek above it.
    <PageScroll contentClassName="pb-6">
      <div className="mb-0.5 flex items-center gap-x-2 pt-6">
        <Input
          icon={<MagnifyingGlassIcon className="h-4 w-4 text-default" />}
          type="search"
          placeholder="Search"
          className="h-9 w-[250px] py-1 pl-9 text-body-sm"
          defaultValue={searchValue}
          onChange={(e) => onSearch?.(e.target.value)}
          autoComplete="off"
        />
        {canEditMapping && (
          <Button variant="primary" size="sm" onClick={onNewMapping}>
            <PlusIcon className="h-4 w-4" strokeWidth={1.5} />
            <span>New Mapping</span>
          </Button>
        )}
      </div>
      {loading ? (
        <div className="grid w-full place-items-center py-20">
          <Spinner size={10} />
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-5">
          <GroupedTableHeaderCard columns={cdtColumns(canEditMapping)} />
          {filteredCategories.map((category: TreatmentCategoryValue) => (
            <GroupedTableSection
              key={`${category}-${disclosureKey}`}
              label={`${category} (${formatRange(category)})`}
              count={(groupedMappings[category]?.length ?? 0) + 1}
              defaultOpen={categoriesWithMappings.has(category)}
            >
              <GroupedTable columns={cdtColumns(canEditMapping)}>
                <TableBody className="divide-y divide-subtle">
                  {/* Every category includes its ADA default range even with no
                      custom mappings: render it as a permanent first row so the
                      section never reads as empty. */}
                  <TableRow>
                    <TableCell className={cn(GROUPED_TABLE_CELL, `text-strong`)}>
                      <span className="flex items-center gap-2">
                        {formatRange(category)}
                        <Tooltip message="All codes in this range map here unless overridden by a custom mapping">
                          <span className="cursor-default rounded border-[0.5px] border-default bg-gray-minimal px-1.5 py-0.5 text-label-xs text-gray-strong">
                            Default
                          </span>
                        </Tooltip>
                      </span>
                    </TableCell>
                    {canEditMapping && <TableCell className={GROUPED_TABLE_CELL} />}
                  </TableRow>
                  {(groupedMappings[category] ?? []).map((mapping: CdtCodeMappingType) => {
                    const isSaving = savingIds.has(mapping.id!)
                    return (
                      <TableRow key={mapping.id}>
                        <TableCell className={cn(GROUPED_TABLE_CELL, `text-strong`)}>{mapping.cdtCode}</TableCell>
                        {canEditMapping && (
                          <TableCell className={cn(GROUPED_TABLE_CELL, `text-right`)}>
                            <div className={cn(`flex justify-end gap-2`, isSaving && `pointer-events-none opacity-40`)}>
                              <Tooltip message={isSaving ? `Saving…` : `Edit mapping`}>
                                <EditPencil
                                  className="aspect-square w-5 cursor-pointer text-default"
                                  onClick={() => onEditMapping(mapping)}
                                />
                              </Tooltip>
                              <Tooltip message={isSaving ? `Saving…` : `Delete mapping`}>
                                <Trash
                                  className="aspect-square w-5 cursor-pointer text-default"
                                  onClick={() => onDeleteMapping(mapping.id!)}
                                />
                              </Tooltip>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </GroupedTable>
            </GroupedTableSection>
          ))}
        </div>
      )}
    </PageScroll>
  )
})
